import { useEffect, useMemo, useState } from 'react';
import { isMoment } from 'moment';
import { getPeriods, updatePeriods } from 'services/api/calendars';
import type { CalendarWithPeriods, Period } from 'services/api/calendars';
import { getColumns } from '../config';

interface UseTableServiceProps {
  calendarId?: string;
}

export const useTableService = ({ calendarId }: UseTableServiceProps) => {
  const [loading, setLoading] = useState(false);
  const [saveIsLoading, setSaveIsLoading] = useState(false);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [calendar, setCalendar] = useState<CalendarWithPeriods>();

  const getTableItems = () => {
    setLoading(true);
    getPeriods({ calendarId: Number(calendarId) })
      .then((res) => {
        setPeriods(res.periods);
        setCalendar(res);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (calendarId) {
      getTableItems();
    }
  }, [calendarId]);

  const handleDeleteRow = (id: number) => {
    setPeriods((prev) => prev.filter((period) => period.id !== id));
  };

  const handleAddRow = () => {
    setPeriods([
      ...periods,
      {
        id: -Date.now(),
        vacationPeriod: false,
        startDate: '',
        endDate: '',
      },
    ]);
  };

  const handleFieldChange = (key: number, field: string, value: any) => {
    setPeriods((prev) =>
      prev.map((row) => {
        if (row.id !== key) {
          return row;
        }

        if (field === 'dateRange') {
          if (!value) {
            return {
              ...row,
              startDate: null,
              endDate: null,
            };
          }

          return {
            ...row,
            startDate: isMoment(value[0]) ? value[0].format('YYYY-MM-DD') : value[0],
            endDate: isMoment(value[1]) ? value[1].format('YYYY-MM-DD') : value[1],
          };
        }

        if (field === 'vacationPeriod') {
          return {
            ...row,
            vacationPeriod: value === 'true',
          };
        }

        return {
          ...row,
          [field]: value,
        };
      }),
    );
  };

  const columns = getColumns(handleFieldChange, handleDeleteRow, calendar?.name);

  const saveIsDisabled = useMemo(() => {
    if (periods.some((period) => !period.startDate || !period.endDate)) {
      return true;
    }

    if (saveIsLoading) {
      return true;
    }

    return false;
  }, [periods, saveIsLoading]);

  const handleSave = async () => {
    try {
      if (calendar) {
        setSaveIsLoading(true);
        await updatePeriods({ calendar: { ...calendar, periods } }).then((res) => {
          setPeriods(res.periods);
          setCalendar(res);
        });
      }
    } finally {
      setSaveIsLoading(false);
    }
  };

  return {
    state: { periods, loading, columns, saveIsLoading, saveIsDisabled },
    methods: { handleAddRow, handleSave },
  };
};
